import { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { QUERY_BADGE_ASSIGNMENTS_BY_FEED } from 'src/_shared/api/graphql/custom/recognition/recognitionQueries';

const useInfiniteBadgeAssignments = ({
	currentUser,
	feedType,
	tags = [],
	pageSize = 25,
	excludeAutomated = false,
}) => {
	const [allBadgeAssignments, setAllBadgeAssignments] = useState([]);
	const [nextToken, setNextToken] = useState(null);
	const [hasMoreData, setHasMoreData] = useState(true);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [dataProcessed, setDataProcessed] = useState(false);
	const apolloClient = useApolloClient();
	const isInitialLoad = useRef(true);
	const previousTags = useRef(tags);
	const previousExcludeAutomated = useRef(excludeAutomated);

	// Reset state synchronously when the query changes after the first load:
	// tags for tag feeds, or excludeAutomated when feature flags change
	useLayoutEffect(() => {
		const tagsChanged =
			feedType === 'tags' &&
			JSON.stringify(previousTags.current) !== JSON.stringify(tags);
		const excludeAutomatedChanged =
			previousExcludeAutomated.current !== excludeAutomated;
		if (tagsChanged || excludeAutomatedChanged) {
			previousTags.current = tags;
			previousExcludeAutomated.current = excludeAutomated;
			setAllBadgeAssignments([]);
			setNextToken(null);
			setHasMoreData(true);
			setIsLoadingMore(false);
			setDataProcessed(false);
			isInitialLoad.current = true;
		}
	}, [tags, feedType, excludeAutomated]);

	const {
		loading: badgeAssignmentsLoading,
		error: badgeAssignmentsError,
		refetch: refetchBadgeAssignments,
	} = useQuery(QUERY_BADGE_ASSIGNMENTS_BY_FEED, {
		variables: {
			companyId: currentUser?.companyId,
			feedType: feedType,
			subCompanyId:
				feedType === 'subCompany' ? currentUser?.subCompanyId : undefined,
			tags: feedType === 'tags' ? tags : null,
			excludeAutomated,
			limit: pageSize,
		},
		skip: !currentUser?.companyId || !feedType,
		fetchPolicy: 'network-only',
		onCompleted: (data) => {
			if (isInitialLoad.current) {
				const items = data?.queryBadgeAssignmentsByFeed?.items || [];
				const token = data?.queryBadgeAssignmentsByFeed?.nextToken;

				setAllBadgeAssignments(items);
				setNextToken(token);
				setHasMoreData(!!token);
				setDataProcessed(true);
				isInitialLoad.current = false;
			}
		},
		onError: (error) => {
			console.log('GraphQL Query Error:', error);
		},
	});

	const loadMoreBadgeAssignments = useCallback(async () => {
		if (!nextToken || isLoadingMore || !currentUser?.companyId) {
			return;
		}

		setIsLoadingMore(true);

		try {
			const result = await apolloClient.query({
				query: QUERY_BADGE_ASSIGNMENTS_BY_FEED,
				variables: {
					companyId: currentUser?.companyId,
					feedType: feedType,
					subCompanyId:
						feedType === 'subCompany' ? currentUser?.subCompanyId : undefined,
					tags: feedType === 'tags' ? tags : null,
					excludeAutomated,
					limit: pageSize,
					nextToken: nextToken,
				},
				fetchPolicy: 'network-only',
			});

			const newItems = result.data?.queryBadgeAssignmentsByFeed?.items || [];
			const newNextToken = result.data?.queryBadgeAssignmentsByFeed?.nextToken;

			setAllBadgeAssignments((prevItems) => {
				return [...prevItems, ...newItems];
			});

			setNextToken(newNextToken);
			setHasMoreData(!!newNextToken);
		} catch (error) {
			console.error('Error loading more badge assignments:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [
		nextToken,
		isLoadingMore,
		currentUser?.companyId,
		currentUser?.subCompanyId,
		feedType,
		tags,
		pageSize,
		excludeAutomated,
		apolloClient,
	]);

	const resetBadgeAssignments = useCallback(async () => {
		try {
			setNextToken(null);
			setHasMoreData(true);
			setIsLoadingMore(false);
			setDataProcessed(false);
			isInitialLoad.current = true;

			if (refetchBadgeAssignments) {
				const result = await refetchBadgeAssignments();

				if (result?.data?.queryBadgeAssignmentsByFeed) {
					const items = result.data.queryBadgeAssignmentsByFeed.items || [];
					const token = result.data.queryBadgeAssignmentsByFeed.nextToken;

					setAllBadgeAssignments(items);
					setNextToken(token);
					setHasMoreData(!!token);
					setDataProcessed(true);
					isInitialLoad.current = false;
				}
			}
		} catch (error) {
			console.error('Error resetting badge assignments:', error);
		}
	}, [refetchBadgeAssignments]);

	const updateBadgeAssignment = useCallback((badgeAssignmentId, updates) => {
		setAllBadgeAssignments((prevItems) =>
			prevItems.map((item) =>
				item.id === badgeAssignmentId ? { ...item, ...updates } : item
			)
		);
	}, []);

	return {
		allBadgeAssignments,
		badgeAssignmentsLoading: badgeAssignmentsLoading || !dataProcessed,
		badgeAssignmentsError,
		hasMoreData,
		isLoadingMore,
		loadMoreBadgeAssignments,
		resetBadgeAssignments,
		refetchBadgeAssignments,
		updateBadgeAssignment,
	};
};

export default useInfiniteBadgeAssignments;
